import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, CreditCard, Check } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// PayPal Client ID - should be from env in production
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || 'test';

const DONATION_AMOUNTS = [10, 25, 50, 100, 250];

export function DonationsPage() {
  const { t, language } = useLanguage();
  const [selectedAmount, setSelectedAmount] = useState(25);
  const [customAmount, setCustomAmount] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const finalAmount = isCustom ? parseFloat(customAmount) || 0 : selectedAmount;

  const handleCustomAmountChange = (e) => {
    setCustomAmount(e.target.value);
    setIsCustom(true);
  };

  const handlePresetClick = (amount) => {
    setSelectedAmount(amount);
    setIsCustom(false);
    setCustomAmount('');
  };

  const createDonation = async (paypalOrderId, status) => {
    try {
      await axios.post(`${API}/donations`, {
        paypal_order_id: paypalOrderId,
        amount: finalAmount,
        currency: 'EUR',
        donor_name: donorName,
        donor_email: donorEmail,
        message: message,
        status: status
      });
    } catch (error) {
      console.error('Error creating donation record:', error);
    }
  };

  const benefits = language === 'it' ? [
    'Sviluppo e chiodatura di nuove vie',
    'Manutenzione delle vie esistenti',
    'Acquisto materiale tecnico',
    'Organizzazione eventi e raduni',
    'Promozione del territorio'
  ] : [
    'Development and bolting of new routes',
    'Maintenance of existing routes',
    'Purchase of technical equipment',
    'Organization of events and meetups',
    'Territory promotion'
  ];

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md mx-auto p-8"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="heading-lg text-[#0A0A0A] mb-4">Grazie!</h2>
          <p className="text-gray-600 mb-8">
            {language === 'it' 
              ? 'La tua donazione è stata ricevuta con successo. Grazie per il tuo supporto!'
              : 'Your donation has been successfully received. Thank you for your support!'}
          </p>
          <Button onClick={() => setSuccess(false)} className="btn-primary">
            {language === 'it' ? 'Fai un\'altra donazione' : 'Make another donation'}
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="donations-page">
      {/* Hero */}
      <section className="relative py-32 bg-[#0A0A0A]">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Heart className="w-16 h-16 text-[#FF6D00] mx-auto mb-6" />
            <h1 className="heading-xl text-white mb-6">
              {t('supportUs')}
            </h1>
            <p className="body-lg text-gray-400 max-w-2xl mx-auto">
              {language === 'it'
                ? 'Il tuo contributo ci aiuta a sviluppare nuovi settori, mantenere le vie esistenti e promuovere l\'arrampicata in Sicilia.'
                : 'Your contribution helps us develop new sectors, maintain existing routes and promote climbing in Sicily.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Donation Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Benefits */}
            <div>
              <h2 className="heading-md text-[#0A0A0A] mb-8">
                {language === 'it' ? 'Come useremo la tua donazione' : 'How we\'ll use your donation'}
              </h2>
              <ul className="space-y-4">
                {benefits.map((benefit, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <div className="w-10 h-10 bg-[#FF6D00]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-5 h-5 text-[#FF6D00]" />
                    </div>
                    <span className="text-gray-700">{benefit}</span>
                  </motion.li>
                ))}
              </ul>

              <div className="mt-12 p-6 bg-gray-50 rounded-lg">
                <h3 className="font-heading text-xl uppercase mb-4">
                  {language === 'it' ? 'Trasparenza totale' : 'Total transparency'}
                </h3>
                <p className="text-gray-600 text-sm">
                  {language === 'it'
                    ? 'Pubblichiamo regolarmente report sull\'utilizzo dei fondi raccolti. Ogni donazione viene registrata e utilizzata esclusivamente per gli scopi indicati.'
                    : 'We regularly publish reports on the use of collected funds. Every donation is recorded and used exclusively for the stated purposes.'}
                </p>
              </div>
            </div>

            {/* Donation Form */}
            <div className="bg-white border rounded-lg p-8">
              <h3 className="font-heading text-2xl uppercase mb-6">
                {language === 'it' ? 'Scegli l\'importo' : 'Choose amount'}
              </h3>

              {/* Preset Amounts */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                {DONATION_AMOUNTS.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => handlePresetClick(amount)}
                    className={`py-4 rounded-lg font-heading text-xl transition-all ${
                      !isCustom && selectedAmount === amount
                        ? 'bg-[#FF6D00] text-white'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                    data-testid={`amount-${amount}`}
                  >
                    €{amount}
                  </button>
                ))}
              </div>

              {/* Custom Amount */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('customAmount')}
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                  <Input
                    type="number"
                    value={customAmount}
                    onChange={handleCustomAmountChange}
                    placeholder="0"
                    className={`pl-8 text-lg ${isCustom ? 'ring-2 ring-[#FF6D00]' : ''}`}
                    data-testid="custom-amount-input"
                  />
                </div>
              </div>

              {/* Donor Info */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('name')} ({language === 'it' ? 'opzionale' : 'optional'})
                  </label>
                  <Input
                    value={donorName}
                    onChange={(e) => setDonorName(e.target.value)}
                    placeholder={language === 'it' ? 'Il tuo nome' : 'Your name'}
                    data-testid="donor-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('email')} ({language === 'it' ? 'opzionale' : 'optional'})
                  </label>
                  <Input
                    type="email"
                    value={donorEmail}
                    onChange={(e) => setDonorEmail(e.target.value)}
                    placeholder={language === 'it' ? 'La tua email' : 'Your email'}
                    data-testid="donor-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('message')} ({language === 'it' ? 'opzionale' : 'optional'})
                  </label>
                  <Input
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={language === 'it' ? 'Lascia un messaggio' : 'Leave a message'}
                    data-testid="donor-message-input"
                  />
                </div>
              </div>

              {/* Total */}
              <div className="flex justify-between items-center py-4 border-t border-b mb-6">
                <span className="font-heading text-lg uppercase">Totale</span>
                <span className="font-heading text-3xl text-[#FF6D00]">€{finalAmount}</span>
              </div>

              {/* PayPal Button */}
              {finalAmount > 0 ? (
                PAYPAL_CLIENT_ID !== 'test' ? (
                  <PayPalScriptProvider options={{ 
                    'client-id': PAYPAL_CLIENT_ID, 
                    currency: 'EUR' 
                  }}>
                    <PayPalButtons
                      style={{ layout: 'vertical', color: 'gold', shape: 'rect' }}
                      createOrder={(data, actions) => {
                        return actions.order.create({
                          purchase_units: [{
                            amount: {
                              value: finalAmount.toString(),
                              currency_code: 'EUR'
                            },
                            description: 'Donazione Caltavuturo Climbing'
                          }]
                        });
                      }}
                      onApprove={async (data, actions) => {
                        const order = await actions.order.capture();
                        await createDonation(order.id, 'completed');
                        setSuccess(true);
                        toast.success(language === 'it' ? 'Donazione completata!' : 'Donation completed!');
                      }}
                      onError={(err) => {
                        console.error('PayPal error:', err);
                        toast.error(language === 'it' ? 'Errore nel pagamento' : 'Payment error');
                      }}
                    />
                  </PayPalScriptProvider>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">
                      {language === 'it' 
                        ? 'PayPal sarà disponibile a breve. Per ora puoi contattarci direttamente.'
                        : 'PayPal will be available soon. For now you can contact us directly.'}
                    </p>
                    <p className="text-sm text-gray-500">
                      info@caltavuturoclimbing.it
                    </p>
                  </div>
                )
              ) : (
                <div className="text-center py-4 text-gray-500">
                  {language === 'it' ? 'Seleziona un importo per continuare' : 'Select an amount to continue'}
                </div>
              )}

              {/* Security Note */}
              <p className="text-xs text-gray-500 text-center mt-6">
                {language === 'it'
                  ? 'Pagamento sicuro gestito da PayPal. Non memorizziamo i dati della tua carta.'
                  : 'Secure payment handled by PayPal. We do not store your card data.'}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
